import { createContext, useContext, useState, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { ProfileSidePanel } from "./ProfileSidePanel";

interface ProfileSidePanelContextValue {
  /** Open the profile side panel for a given user */
  openProfile: (userId: string) => void;
  /** Close the profile side panel */
  closeProfile: () => void;
  /** Whether the profile side panel is currently open */
  isOpen: boolean;
  /** The currently displayed user ID (if any) */
  currentUserId: string | null;
}

const ProfileSidePanelContext = createContext<ProfileSidePanelContextValue | null>(null);

/**
 * Hook to access the profile side panel context.
 * Use this to open profiles in a side panel from anywhere in the app.
 */
export function useProfileSidePanel() {
  const context = useContext(ProfileSidePanelContext);
  if (!context) {
    throw new Error("useProfileSidePanel must be used within a ProfileSidePanelProvider");
  }
  return context;
}

interface ProfileSidePanelProviderProps {
  children: ReactNode;
}

/**
 * Provider that enables opening user profiles in a side panel from anywhere in the app.
 * Wrap your app root with this provider to enable the functionality.
 */
export function ProfileSidePanelProvider({ children }: ProfileSidePanelProviderProps) {
  const [userId, setUserId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openProfile = useCallback((newUserId: string) => {
    setUserId(newUserId);
    setIsOpen(true);
  }, []);

  const closeProfile = useCallback(() => {
    setIsOpen(false);
    // Delay clearing userId to allow close animation to complete
    setTimeout(() => setUserId(null), 300);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      closeProfile();
    }
  }, [closeProfile]);

  const value = useMemo(
    () => ({
      openProfile,
      closeProfile,
      isOpen,
      currentUserId: userId,
    }),
    [openProfile, closeProfile, isOpen, userId]
  );

  return (
    <ProfileSidePanelContext.Provider value={value}>
      {children}
      <ProfileSidePanel
        userId={userId}
        open={isOpen}
        onOpenChange={handleOpenChange}
      />
    </ProfileSidePanelContext.Provider>
  );
}
